import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Plus, Minus, Trash, ShoppingBag, TestTube } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useState } from "react";
import { openRazorpayCheckout } from "@/lib/razorpay";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, subtotal, updateQuantity, removeItem, clearCart, count } = useCart();
  const { user, openAuth } = useAuth();
  const [busy, setBusy] = useState(false);

  const onCheckout = async () => {
    if (items.length === 0 || busy) return;
    if (!user) {
      toast.info("Sign in to complete checkout");
      openAuth("signin");
      return;
    }
    setBusy(true);
    try {
      // INR is Razorpay's default supported currency for test mode.
      // Amount is subtotal converted to paise.
      const amountPaise = Math.round(subtotal * 100);
      const result = await openRazorpayCheckout({
        amount: amountPaise,
        currency: "INR",
        name: "Aeonyx Life Sciences",
        description: `${count} item${count === 1 ? "" : "s"} — CryoVault checkout`,
        prefill: {
          email: user.email,
          name: user.user_metadata?.full_name || "",
          contact: user.user_metadata?.phone || "",
        },
        notes: {
          user_id: user.id,
          user_email: user.email,
          items_count: String(count),
        },
      });
      toast.success("Payment successful", {
        description: `Payment ID: ${result.payment_id}`,
      });
      clearCart();
      setIsOpen(false);
    } catch (err) {
      const msg = err?.message || "Payment failed";
      if (msg === "Payment cancelled") toast.info("Payment cancelled");
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md flex flex-col p-0 bg-white border-l border-[#8A97A8]/20"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#8A97A8]/20">
          <SheetTitle className="font-display text-navy-deep text-2xl flex items-center gap-2">
            <ShoppingBag size={22} weight="regular" />
            Your Cart
            <span
              data-testid="cart-drawer-count"
              className="ml-1 text-sm font-mono-data text-cool-grey font-normal"
            >
              ({count})
            </span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div
            data-testid="cart-empty"
            className="flex-1 flex flex-col items-center justify-center text-center px-8 py-16 gap-3"
          >
            <div className="h-16 w-16 rounded-full bg-cool-white grid place-items-center border border-[#8A97A8]/25">
              <ShoppingBag size={26} weight="thin" className="text-cool-grey" />
            </div>
            <div className="font-display text-lg text-navy-deep">Your cart is empty</div>
            <p className="text-sm text-cool-grey max-w-[240px]">
              Browse research-grade peptides and curated stacks to add them here.
            </p>
            <Button
              data-testid="cart-empty-close"
              onClick={() => setIsOpen(false)}
              className="mt-3 h-10 px-5 rounded-full bg-navy-deep hover:bg-navy text-white text-[13px]"
            >
              Continue browsing
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((it) => (
                <div
                  key={it.id}
                  data-testid={`cart-item-${it.id}`}
                  className="flex gap-3 pb-4 border-b border-[#8A97A8]/15 last:border-0"
                >
                  <div className="h-16 w-16 shrink-0 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 grid place-items-center">
                    <TestTube size={26} weight="thin" className="text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div className="min-w-0">
                        <h4 className="font-display font-semibold text-navy-deep text-[15px] truncate">
                          {it.name}
                        </h4>
                        <div className="font-mono-data text-[10.5px] text-cool-grey mt-0.5 truncate">
                          {it.sku}
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(it.id)}
                        data-testid={`cart-remove-${it.id}`}
                        aria-label={`Remove ${it.name}`}
                        className="shrink-0 h-8 w-8 grid place-items-center rounded-full text-cool-grey hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash size={15} weight="regular" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center rounded-full border border-[#8A97A8]/30">
                        <button
                          onClick={() => updateQuantity(it.id, it.quantity - 1)}
                          data-testid={`cart-decrement-${it.id}`}
                          aria-label="Decrease quantity"
                          className="h-8 w-8 grid place-items-center text-navy-deep hover:text-cyan-brand"
                        >
                          <Minus size={12} weight="bold" />
                        </button>
                        <span className="w-7 text-center font-mono-data text-[13px] text-navy-deep tabular-nums">
                          {it.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(it.id, it.quantity + 1)}
                          data-testid={`cart-increment-${it.id}`}
                          aria-label="Increase quantity"
                          className="h-8 w-8 grid place-items-center text-navy-deep hover:text-cyan-brand"
                        >
                          <Plus size={12} weight="bold" />
                        </button>
                      </div>
                      <div className="font-mono-data text-navy-deep font-semibold text-[15px] tabular-nums">
                        ${(it.price * it.quantity).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#8A97A8]/20 px-6 py-5 space-y-4 bg-white">
              <div className="flex items-center justify-between">
                <span className="text-sm text-cool-grey">Subtotal</span>
                <span
                  data-testid="cart-subtotal"
                  className="font-mono-data text-navy-deep font-bold text-xl tabular-nums"
                >
                  ${subtotal.toFixed(2)}
                </span>
              </div>
              <p className="text-[11px] text-cool-grey leading-relaxed">
                Taxes and shipping calculated at checkout. Research use only.
              </p>
              <Button
                onClick={onCheckout}
                disabled={busy}
                data-testid="cart-checkout"
                className="w-full h-12 rounded-full bg-cta-gradient hover:brightness-110 text-white font-semibold shadow-[0_8px_22px_-8px_rgba(1,207,198,0.55)]"
              >
                {busy ? "Opening secure checkout…" : `Checkout — ₹${subtotal.toFixed(2)}`}
              </Button>
              <button
                onClick={clearCart}
                data-testid="cart-clear"
                className="w-full text-center text-[12px] text-cool-grey hover:text-red-500 transition-colors"
              >
                Clear cart
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
